import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaFilePdf, FaUpload } from "react-icons/fa";
import { Drawer } from "../../shared";
import { TextInput, Textarea, CustomDropdown, Checkbox } from "../../atoms";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  usePresignFactoryDocumentMutation,
  useCreateFactoryDocumentMutation,
  useUpdateFactoryDocumentMutation,
} from "../../../store";

/** V1 sınırı — sunucu da aynı sınırı uygular (25 MB, yalnız PDF). */
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const PDF_MIME = "application/pdf";

const formatSize = (bytes) => {
  if (!bytes) return "-";
  const mb = bytes / (1024 * 1024);
  return mb >= 1
    ? `${mb.toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
};

const PHASE_LABEL = {
  presigning: "Adres alınıyor...",
  uploading: "Yükleniyor",
  confirming: "Kaydediliyor...",
};

/**
 * Fabrika belgesi yükleme / düzenleme çekmecesi (D-071).
 *
 * Yükleme akışı Hardware Update ile AYNI: presign → dosya doğrudan
 * depolamaya PUT → create. Düzenleme modunda dosya değişmez; yalnız
 * başlık/açıklama/grup/sürüm/aktiflik güncellenir (V1'de sürüm geçmişi yok).
 *
 * Grup seçicide HAM izin anahtarı gösterilmez — Türkçe etiket gösterilir.
 */
export default function FactoryDocumentDrawer({
  open,
  onClose,
  document: editDocument,
  groups = [],
  groupLabels = {},
}) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();

  const isEdit = !!editDocument?._id;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [permission, setPermission] = useState("");
  const [version, setVersion] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const xhrRef = useRef(null);
  const fileInputRef = useRef(null);

  const [presign] = usePresignFactoryDocumentMutation();
  const [createDocument] = useCreateFactoryDocumentMutation();
  const [updateDocument] = useUpdateFactoryDocumentMutation();

  useEffect(() => {
    if (!open) return;
    setTitle(editDocument?.title || "");
    setDescription(editDocument?.description || "");
    // Tek grup varsa yeni belgede önceden seçili gelsin.
    setPermission(
      editDocument?.permission || (groups.length === 1 ? groups[0].key : "")
    );
    setVersion(editDocument?.version || "");
    setIsActive(editDocument?.isActive !== false);
    setFile(null);
    setPhase("idle");
    setProgress(0);
  }, [open, editDocument, groups]);

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        id: g.key,
        label: groupLabels[g.key] || g.key,
        description: g.description || "",
      })),
    [groups, groupLabels]
  );

  const busy = phase !== "idle";

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.type !== PDF_MIME) {
      showError("Yalnızca PDF yüklenebilir");
      e.target.value = "";
      return;
    }
    if (picked.size > MAX_FILE_SIZE_BYTES) {
      showError("Dosya boyutu 25 MB sınırını aşıyor");
      e.target.value = "";
      return;
    }
    setFile(picked);
    if (!title.trim()) setTitle(picked.name.replace(/\.pdf$/i, ""));
  };

  const uploadToStorage = (url, picked) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Yükleme başarısız: HTTP ${xhr.status}`));
      xhr.onerror = () => reject(new Error("Bağlantı hatası"));
      xhr.ontimeout = () => reject(new Error("Zaman aşımı"));
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", PDF_MIME);
      xhr.send(picked);
    });

  const handleSubmit = async () => {
    if (!title.trim()) return showError("Başlık zorunludur");
    if (!permission) return showError("Belge grubu seçiniz");
    if (!isEdit && !file) return showError("PDF dosyası seçiniz");

    try {
      if (isEdit) {
        await updateDocument({
          token,
          id: editDocument._id,
          title: title.trim(),
          description: description.trim(),
          permission,
          version: version.trim(),
          isActive,
        }).unwrap();
        showSuccess("Belge güncellendi");
        onClose();
        return;
      }

      setPhase("presigning");
      setProgress(0);
      const presignResult = await presign({
        token,
        fileName: file.name,
        mimeType: PDF_MIME,
      }).unwrap();
      const { url, storageKey } = presignResult?.data || {};
      if (!url || !storageKey) throw new Error("Yükleme adresi alınamadı");

      setPhase("uploading");
      await uploadToStorage(url, file);
      xhrRef.current = null;

      setPhase("confirming");
      await createDocument({
        token,
        title: title.trim(),
        description: description.trim(),
        permission,
        storageKey,
        fileName: file.name,
        fileSize: file.size,
        mimeType: PDF_MIME,
        version: version.trim(),
        isActive,
      }).unwrap();

      showSuccess("Belge yüklendi");
      onClose();
    } catch (err) {
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }
      showError(err?.data?.message || err?.message || "Belge kaydedilemedi");
    } finally {
      setPhase("idle");
      setProgress(0);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={busy ? undefined : onClose}
      title={isEdit ? "Belgeyi Düzenle" : "Belge Yükle"}
      subtitle={
        isEdit
          ? "Dosya değişmez — bilgileri güncellersiniz"
          : "Tablette kart okutan çalışan bu PDF'i görecek"
      }
      titleIcon={FaFilePdf}
      width="md"
    >
      <Drawer.Body className="p-4 sm:p-5 space-y-4">
        <TextInput
          label="Başlık"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Örn. Askı Kartı Montaj Kılavuzu"
        />

        <Textarea
          label="Açıklama"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Kısa açıklama (opsiyonel)"
          rows={2}
        />

        <CustomDropdown
          label="Grup"
          required
          value={groupOptions.find((o) => o.id === permission) || null}
          onChange={(opt) => setPermission(opt?.id || "")}
          options={groupOptions}
          placeholder="Belge grubu seçiniz"
          showSearch={false}
          emptyMessage="Tanımlı belge grubu yok"
        />

        <TextInput
          label="Sürüm"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="Örn. 2 (serbest metin)"
        />

        {!isEdit && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              PDF Dosyası <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-2xl text-left shadow-sm hover:shadow-md transition-all disabled:opacity-60"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-red-50 text-red-600 shrink-0">
                <FaFilePdf className="w-5 h-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-gray-800 truncate">
                  {file ? file.name : "Dosya seçiniz"}
                </span>
                <span className="block text-xs text-gray-400">
                  {file ? formatSize(file.size) : "Yalnızca PDF · en fazla 25 MB"}
                </span>
              </span>
              <FaUpload className="w-4 h-4 text-gray-400 shrink-0" />
            </button>

            {phase === "uploading" && (
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="pt-1">
          <Checkbox
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <p className="mt-1 text-xs text-gray-400">
            Pasif belge tablette hiç görünmez.
          </p>
        </div>
      </Drawer.Body>

      <Drawer.Footer className="p-4 border-t border-gray-100 bg-white flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          Vazgeç
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy}
          className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold transition-colors disabled:opacity-60"
        >
          {busy
            ? `${PHASE_LABEL[phase]}${phase === "uploading" ? ` %${progress}` : ""}`
            : isEdit
              ? "Kaydet"
              : "Yükle"}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
}
